import { Input } from '../../common/input.component';
import {
  CreateCurrencyV2,
  CURRENCY_TYPE_LABELS,
  CurrencyService,
  useCurrencyCurrent,
} from '../../../services/currency.service';
import {
  CleanCurrencyCurrentV2,
  PartnerRateJumpLimit,
} from '../../../export-types/cleaned-types';
import { AlertService } from '../../../services/alert.service';
import { useForm } from 'react-hook-form';
import React from 'react';
import { ActualCurrency } from './ActualCurrency';
import { ModalProps } from '../../../services/modals.service';
import { Decimal } from 'decimal.js';
import { DECIMAL_PLACES, DECIMAL_STEP } from '../../../utils/common.utils';
import { useGlobalMutate } from '../../../api/useGlobalMutate';
import { useModalClose } from '../../../utils/useModalClose';

type FieldValues = CreateCurrencyV2 & {
  usdRubCbr: string;
  cnyRubCbr: string;
};

// Курсы партнёра, которые обновляет крон Я Обмена. По ним сразу считаются
// пополнения, а доллар со скачком от PartnerRateJumpLimit крон считает сбоем
// выгрузки и не перезаписывает, так что опечатка (лишний или пропущенный знак)
// сама не исправится. Юань крон пересчитывает из доллара, но только когда
// меняется доллар.
const PARTNER_RATES = [
  {
    usdField: 'usdRubPartnerP2P',
    cnyField: 'cnyRubPartnerP2P',
    label: 'Перевод партнёру',
  },
  { usdField: 'usdRubPartner', cnyField: 'cnyRubPartner', label: 'Наличные' },
] as const;

type PartnerRate = (typeof PARTNER_RATES)[number];

const PARTNER_RATE_FIELDS = PARTNER_RATES.flatMap((rate) => [
  rate.usdField,
  rate.cnyField,
]);

function partnerRateJumps(form: FieldValues, current: CleanCurrencyCurrentV2) {
  return PARTNER_RATE_FIELDS.flatMap((field) => {
    const entered = form[field];
    const was = current[field];
    if (!entered || !was || Number(was) <= 0) {
      return [];
    }
    const change = new Decimal(entered).div(was).minus(1);
    return change.abs().gte(PartnerRateJumpLimit)
      ? [
          `${CURRENCY_TYPE_LABELS[field]}: ${was} → ${entered} (${change
            .mul(100)
            .toFixed(0)}%)`,
        ]
      : [];
  });
}

export const CurrencyModal = ({ close }: ModalProps) => {
  const {
    register,
    formState: { errors },
    handleSubmit,
    getValues,
    setValue,
  } = useForm<FieldValues>();

  const { mutate: globalMutate } = useGlobalMutate();
  const { data: current } = useCurrencyCurrent();

  function onSave(currency: FieldValues) {
    const enteredPartnerRate = PARTNER_RATE_FIELDS.some(
      (field) => currency[field],
    );
    // Без текущих курсов сравнить не с чем, а пропустить проверку молча нельзя.
    if (enteredPartnerRate && !current) {
      AlertService.warning(
        'Текущие курсы ещё не загрузились, сохраните чуть позже',
      );
      return;
    }
    const jumps = current ? partnerRateJumps(currency, current) : [];
    if (
      jumps.length > 0 &&
      !window.confirm(
        `Курс отличается от текущего на ${PartnerRateJumpLimit * 100}% и больше:\n` +
          `${jumps.join('\n')}\n\n` +
          'Проверьте, нет ли опечатки: по этому курсу сразу начнут считаться ' +
          'пополнения, а доллар с таким скачком крон Я Обмена сам не исправит. ' +
          'Сохранить?',
      )
    ) {
      return;
    }

    CurrencyService.createCurrency(currency).then(async () => {
      AlertService.success();
      close();
      await globalMutate();
    });
  }

  // Юань партнёра считаем из доллара через кросс-курс Китая из формы — так же
  // крон считает его по сохранённым курсам Китая.
  function calculateCnyRub({ usdField, cnyField, label }: PartnerRate) {
    const values = getValues();
    const usdRub = values[usdField];
    const { usdRubExternal, cnyRubExternal } = values;
    // Поле type="number" с невалидным вводом отдаёт пустую строку, так что
    // эта проверка отсекает и пустые, и невалидные значения до Decimal.
    if (!usdRub || !usdRubExternal || !cnyRubExternal) {
      AlertService.warning(
        `Для расчёта заполните USD/RUB ${label}, CNY/RUB Китай и USD/RUB Китай`,
      );
      return;
    }

    const value = new Decimal(usdRub)
      .div(new Decimal(usdRubExternal).div(new Decimal(cnyRubExternal)))
      .toFixed(DECIMAL_PLACES);
    setValue(cnyField, value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }

  const modalRef = useModalClose(close);

  return (
    <div className="modal-dialog" ref={modalRef}>
      <form className="modal-content" onSubmit={handleSubmit(onSave)}>
        <div className="modal-body">
          <div className="d-flex flex-row justify-content-between align-items-center">
            <h3>Добавить курс валют</h3>
            <div className="btn p-0" onClick={close}>
              <i className="bi bi-x fs-36 color-gray-600" />
            </div>
          </div>
          <ActualCurrency />
          <h4>Курс на пополнение CNY</h4>
          <div className="row mb-2">
            <div className="col-3">
              <Input
                type="number"
                min={0}
                step={DECIMAL_STEP}
                {...register('cnyRubExternal', {})}
                error={errors.cnyRubExternal?.message}
                title="CNY/RUB Китай"
                postfix="₽"
              />
            </div>
            <div className="col-3">
              <Input
                type="number"
                min={0}
                step={DECIMAL_STEP}
                {...register('cnyRubInternal', {})}
                error={errors.cnyRubInternal?.message}
                title="CNY/RUB Сайт"
                postfix="₽"
              />
            </div>
            <div className="col">
              <Input
                type="number"
                min={0}
                step={DECIMAL_STEP}
                {...register('cnyRubPartnerP2P', {})}
                error={errors.cnyRubPartnerP2P?.message}
                title="CNY/RUB Перевод партнёру"
                postfix="₽"
              />
            </div>
            <div className="col">
              <Input
                type="number"
                min={0}
                step={DECIMAL_STEP}
                {...register('cnyRubPartner', {})}
                error={errors.cnyRubPartner?.message}
                title="CNY/RUB Наличные"
                postfix="₽"
              />
            </div>
          </div>
          <p className="text-end">
            {PARTNER_RATES.map((rate) => (
              <button
                key={rate.cnyField}
                className="btn btn-light btn-sm ms-1"
                onClick={() => calculateCnyRub(rate)}
                tabIndex={-1}
                type="button"
              >
                Рассчитать {CURRENCY_TYPE_LABELS[rate.cnyField]}
              </button>
            ))}
          </p>

          <h4>Курс на пополнение USD</h4>
          <div className="row">
            <div className="col">
              <Input
                className="mb-4"
                type="number"
                min={0}
                step={DECIMAL_STEP}
                {...register('usdRubExternal', {})}
                error={errors.usdRubExternal?.message}
                title="USD/RUB Китай"
                postfix="₽"
              />
            </div>
            <div className="col">
              <Input
                className="mb-4"
                type="number"
                min={0}
                step={DECIMAL_STEP}
                {...register('usdRubInternal', {})}
                error={errors.usdRubInternal?.message}
                title="USD/RUB Сайт"
                postfix="₽"
              />
            </div>
            <div className="col">
              <Input
                className="mb-4"
                type="number"
                min={0}
                step={DECIMAL_STEP}
                {...register('usdRubPartnerP2P', {})}
                error={errors.usdRubPartnerP2P?.message}
                title="USD/RUB Перевод партнёру"
                postfix="₽"
              />
            </div>
            <div className="col">
              <Input
                className="mb-4"
                type="number"
                min={0}
                step={DECIMAL_STEP}
                {...register('usdRubPartner', {})}
                error={errors.usdRubPartner?.message}
                title="USD/RUB Наличные"
                postfix="₽"
              />
            </div>
          </div>

          <h4>Курс обмена</h4>
          <div className="row">
            <div className="col">
              <Input
                className="mb-4"
                type="number"
                min={0}
                step={DECIMAL_STEP}
                {...register('usdCny', {})}
                error={errors.usdCny?.message}
                title="USD/CNY Кросс"
              />
            </div>
            <div className="col">
              <Input
                className="mb-4"
                type="number"
                min={0}
                step={DECIMAL_STEP}
                {...register('cnyUsd', {})}
                error={errors.cnyUsd?.message}
                title="CNY/USD Кросс"
              />
            </div>
            <div className="col"></div>
          </div>
          <h4>Курс ЦБ</h4>
          <div className="row">
            <div className="col">
              <Input
                className="mb-4"
                type="number"
                min={0}
                step={DECIMAL_STEP}
                {...register('usdRubCbr', {})}
                error={errors.usdRubCbr?.message}
                title="USD/RUB Курс ЦБ"
              />
            </div>
            <div className="col">
              <Input
                className="mb-4"
                type="number"
                min={0}
                step={DECIMAL_STEP}
                {...register('cnyRubCbr', {})}
                error={errors.cnyRubCbr?.message}
                title="CNY/RUB Курс ЦБ"
              />
            </div>
            <div className="col"></div>
          </div>
        </div>
        <div className="modal-footer justify-content-end">
          <button type="submit" className="btn btn-success">
            Добавить
          </button>
        </div>
      </form>
    </div>
  );
};
